import React from "react";
import { ASSETS } from "@/lib/assets";
import { RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Stat } from "./shared";

const ShareCardButton = ({ logId }) => {
  const url = `${window.location.origin}/card/battle/${logId}`;
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Share link copied — paste anywhere.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      window.open(url, "_blank");
    }
  };
  return (
    <button onClick={copy} className="btn-ghost inline-flex items-center gap-2"
            data-testid="battle-share-button">
      <Sparkles className="w-3 h-3" /> {copied ? "COPIED" : "SHARE THIS REPORT"}
    </button>
  );
};

export const ResultView = ({ result, onReset, navigate, isPersonal }) => {
  const won = isPersonal ? result.squad_won : result.won;
  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-16">
      <p className={`tac-label fade-up ${won ? "text-[#1B7D5E]" : "text-[var(--danger)]"}`}
         data-testid="battle-outcome-label">
        {isPersonal
          ? (won ? "SQUAD VICTORY // YOU LIVED" : result.wounded ? "SQUAD DEFEAT // WOUNDED" : "SQUAD DEFEAT // YOU LIVED")
          : result.retreated ? "DEFEAT // YOU PULLED BACK"
          : won ? "VICTORY // THE REGION GROANS" : "DEFEAT // THE LINE BROKE"}
      </p>
      <h1 className="font-serif-h text-5xl lg:text-7xl uppercase text-bone leading-none mt-3 fade-up"
          data-testid="battle-result-heading">
        {result.battle_name}
      </h1>

      {isPersonal && (
        <div className="mt-3 fade-up delay-1">
          <div className="tactical-card border-l-2"
               style={{ borderLeftColor:
                 result.outcome === "critical" ? "#7FBA9D"
                 : result.outcome === "disaster" ? "#FF3B30"
                 : result.outcome === "failure" ? "#B30000"
                 : "#4A0000" }}>
            <p className="tac-label" data-testid="result-outcome-class">OUTCOME // {result.outcome?.toUpperCase()}</p>
            <p className="text-[0.7rem] text-muted-h mt-1 leading-relaxed">
              {result.outcome === "critical" && "Clean strike. The squad noticed."}
              {result.outcome === "success" && "It went how you planned."}
              {result.outcome === "failure" && "It went wrong. The battlefield betrayed you."}
              {result.outcome === "disaster" && "Catastrophic. You barely walked back."}
            </p>
          </div>
        </div>
      )}

      {isPersonal ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 fade-up delay-1">
            <Stat label="KILLS" value={result.kills} />
            <Stat label="WOUNDED" value={result.wounded ? "YES" : "NO"} bad={result.wounded} />
            <Stat label="RECRUITS" value={result.recruit} good={result.recruit > 0} />
            <Stat label="JOINED YOU" value={result.own_converted ?? result.converted} good={(result.own_converted ?? result.converted) > 0} />
          </div>
          {result.given_to_superior > 0 && (
            <div className="mt-3 fade-up delay-1">
              <div className="tactical-card border-l-2 border-[var(--border-blood)]">
                <p className="tac-label">SUPERIOR'S CUT</p>
                <p className="text-sm text-bone mt-1" data-testid="result-superior-cut">
                  {result.given_to_superior} of the {result.converted} you converted joined your Squad Leader's horde, not yours. Promotion brings them under your direct command.
                </p>
              </div>
            </div>
          )}
          {result.tactic?.verdict && result.tactic?.reasoning && (
            <div className="mt-3 fade-up delay-1">
              <div className="tactical-card border-l-2 border-blood"
                   data-testid="custom-tactic-verdict">
                <p className="tac-label">
                  SQUAD LEADER'S JUDGEMENT — {result.tactic.verdict.toUpperCase()}
                </p>
                <p className="text-sm text-bone mt-1 italic" data-testid="custom-tactic-reasoning">
                  "{result.tactic.reasoning}"
                </p>
                <div className="flex gap-4 mt-2 text-[0.65rem] font-mono text-muted-h">
                  {result.custom_rep_delta !== 0 && (
                    <span data-testid="custom-rep-delta">
                      Reputation: <span className={result.custom_rep_delta > 0 ? "text-bone" : "text-blood"}>
                        {result.custom_rep_delta > 0 ? "+" : ""}{result.custom_rep_delta}
                      </span>
                    </span>
                  )}
                  {result.custom_horde_loss > 0 && (
                    <span data-testid="custom-horde-loss" className="text-blood">
                      −{result.custom_horde_loss} zombie{result.custom_horde_loss > 1 ? "s" : ""} lost to the call
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="mt-3 fade-up delay-1">
            <div className="tactical-card">
              <p className="tac-label">YOUR INFLUENCE ON THE WIDER BATTLE</p>
              <p className="font-mono text-xs text-bone mt-1">
                {Math.round((result.contribution_weight || 0) * 100)}% — base squad win-rate {Math.round((result.base_squad_win || 0) * 100)}%
              </p>
              <p className="text-[0.7rem] text-muted-h mt-2 leading-relaxed">
                The bigger the enemy, the less one zombie can sway the outcome. Climb ranks to command the swing yourself.
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10 fade-up delay-1">
          <Stat label="COMMITTED" value={result.committed} />
          <Stat label="FALLEN" value={result.zombie_casualties} bad />
          <Stat label="HUMANS KILLED" value={result.human_kills} />
          <Stat label="CONVERTED" value={result.converted} good />
        </div>
      )}

      {!isPersonal && (result.deserted > 0 || result.sallied) && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 fade-up delay-1">
          {result.deserted > 0 && <Stat label="DESERTED" value={result.deserted} good />}
          {result.deserter_caught > 0 && <Stat label="CAUGHT IN FLIGHT" value={result.deserter_caught} good />}
          {result.sallied && <Stat label="SALLIED" value="YES" bad />}
        </div>
      )}

      {result.officer_bonus_pct > 0 && (
        <div className="mt-4 flex items-center gap-2 text-xs text-muted-h"
             data-testid="result-officer-bonus">
          <span className="font-mono text-[0.6rem] tracking-widest text-[#7FBA9D]">
            +{Math.round(result.officer_bonus_pct * 100)}% CONVERSION
          </span>
          <span className="italic">
            · {result.officer_bonus_count} named officer{result.officer_bonus_count === 1 ? "" : "s"} bit alongside you
          </span>
        </div>
      )}

      <div className="mt-10 narrative-panel fade-up delay-2 relative">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <img src={ASSETS.bone} alt="" className="w-full h-full object-cover mix-blend-overlay" />
        </div>
        <p className="tac-label mb-3 relative">FIELD REPORT</p>
        <p className="text-base lg:text-lg leading-relaxed text-bone whitespace-pre-line relative" data-testid="result-narrative">
          {result.narrative}
        </p>
      </div>

      {(result.newly_spawned_officers?.length > 0) && (
        <div className="mt-6 narrative-panel border-l-2 border-[#7FBA9D]/60"
             data-testid="result-officer-spawned">
          <p className="tac-label mb-2">A DEAD ONE HAS REMEMBERED ITS NAME</p>
          {result.newly_spawned_officers.map((o) => (
            <p key={o.id} className="font-serif-h text-xl text-bone leading-tight mt-1">
              {o.name}
              <span className="ml-3 font-mono text-[0.6rem] tracking-widest text-[#7FBA9D]/80 align-middle">
                {(o.specialty || "").toUpperCase()}
              </span>
            </p>
          ))}
          <p className="text-[0.7rem] text-muted-h italic mt-2 leading-snug">
            They walk with you now. Commit them wisely — the ones who wake once can also die once.
          </p>
        </div>
      )}

      {result.officer_lost && (
        <div className="mt-6 narrative-panel border-l-2 border-blood"
             data-testid="result-officer-lost">
          <p className="tac-label mb-2">A NAME HAS FALLEN</p>
          <p className="font-serif-h text-2xl text-bone leading-tight">{result.officer_lost.name}</p>
          <p className="font-mono text-[0.6rem] text-muted-h tracking-widest mt-1">
            {(result.officer_lost.specialty || "").toUpperCase()} · ENDED AT {result.officer_lost.died_battle || "the field"}
          </p>
          {result.officer_lost.epitaph && (
            <p className="text-sm text-muted-h italic mt-3 leading-relaxed">
              "{result.officer_lost.epitaph}"
            </p>
          )}
        </div>
      )}

      {result.promotion_offered && (
        <div className="mt-6 narrative-panel" data-testid="promo-noticed">
          <p className="tac-label">NOTICED</p>
          <p className="font-serif-h text-2xl text-bone mt-1">The Squad Leader watched.</p>
          <p className="text-sm text-[var(--text-main)] mt-3">
            Return to Command to accept your promotion — or stay where you are.
          </p>
        </div>
      )}

      <div className="mt-12 flex flex-wrap gap-3">
        <button onClick={onReset} className="btn-blood" data-testid="next-battle-button">
          <RotateCcw className="w-3 h-3" /> NEXT BATTLE
        </button>
        <button onClick={() => navigate("/command")} className="btn-ghost" data-testid="result-back-command">
          RETURN TO COMMAND
        </button>
        {result.log_id && (
          <ShareCardButton logId={result.log_id} />
        )}
      </div>
    </div>
  );
};
